import { useState } from "react";
import { Button, Grid, Typography, useTheme, Box, Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import { connect, ConnectedProps } from "react-redux";
import { AnyAction, bindActionCreators, Dispatch } from "redux";
import { UserDto } from "../../store/models/user/UserDto";
import { thunkRemoveUserOtpDevice } from "../../store/actions/UserActions";
import { useTranslation } from "react-i18next";
import { Delete, PhoneAndroid, Security } from "@mui/icons-material";
import { formatUtcToLocalDateTimeInGermanFormat } from "../atoms/Utils";

interface MfaConfigurationProps {
  user: UserDto;
}

const MfaConfiguration = (props: MfaConfigurationProps & ThunkProps) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [isLoading] = useState(false);
  const [processingDeviceId, setProcessingDeviceId] = useState<string | null>(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [deviceToDelete, setDeviceToDelete] = useState<{id: string, label: string, createdDate?: number | string} | null>(null);

  const { user, thunkRemoveUserOtpDevice } = props;

  const openConfirmDialog = (deviceId: string, deviceLabel: string, createdDate?: number | string) => {
    setDeviceToDelete({ 
      id: deviceId, 
      label: deviceLabel || t("user.mfa.unknownDevice"),
      createdDate 
    });
    setConfirmDialogOpen(true);
  };

  const closeConfirmDialog = () => {
    setConfirmDialogOpen(false);
    setDeviceToDelete(null);
  };

  const handleRemoveOtp = async (deviceId: string) => {
    closeConfirmDialog();
    setProcessingDeviceId(deviceId);
    try {
      await thunkRemoveUserOtpDevice(user, deviceId);
    } finally {
      setProcessingDeviceId(null);
    }
  };

  const formatDate = (timestamp?: number | string) => {
    if (!timestamp) return "";
    
    try {
      const isoString = typeof timestamp === 'number' 
        ? new Date(timestamp).toISOString() 
        : timestamp;
      
      return formatUtcToLocalDateTimeInGermanFormat(isoString);
    } catch {
      return "";
    }
  };

  if (isLoading) {
    return (
      <Box>
        <Typography>{t("user.mfa.loading")}</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography fontWeight="bold">
            {t("user.mfa.title")}
          </Typography>
        </Grid>
        
        {user.otpDevices && user.otpDevices.length > 0 ? (
          <>
            <Grid item xs={12} style={{ marginBottom: theme.spacing(1) }}>
              <Typography variant="subtitle1">
                {t("user.mfa.connectedDevices", { count: user.otpDevices.length })}
              </Typography>
            </Grid>
            
            {user.otpDevices.map((device) => (
              <Grid item xs={12} key={device.id}>
                <Box 
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    padding: 2,
                    backgroundColor: 'background.paper',
                    marginBottom: 2,
                    display: 'inline-block',
                    minWidth: '400px',
                    maxWidth: '600px'
                  }}
                >
                  <Box 
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      maxWidth: '500px'
                    }}
                  >
                    <Box sx={{ flexGrow: 0 }}>
                      <Typography variant="subtitle2" component="div">
                        {device.userLabel ?? t("user.mfa.unknownDevice")}
                      </Typography>
                      {device.createdDate && (
                        <Typography variant="body2" color="textSecondary">
                          {t("user.mfa.createdOn")}: {formatDate(device.createdDate)}
                        </Typography>
                      )}
                    </Box>
                    <Button
                      color="error"
                      onClick={() => openConfirmDialog(device.id, device.userLabel, device.createdDate)}
                      disabled={isLoading ?? processingDeviceId === device.id}
                      aria-label={t("user.mfa.removeDevice")}
                      sx={{ minWidth: 'auto', width: '40px', height: '40px', flexShrink: 0, marginLeft: 'auto' }}
                    >
                      {processingDeviceId === device.id 
                      ? "..." 
                      : <Delete sx={{ fontSize: 32 }} />
                      }
                    </Button>
                  </Box>
                </Box>
              </Grid>
            ))}
          </>
        ) : (
          <Grid item xs={12}>
            <Typography>{t("user.mfa.noDeviceConnected")}</Typography>
          </Grid>
        )}
      </Grid>

      <Dialog
        open={confirmDialogOpen}
        onClose={closeConfirmDialog}
        aria-labelledby="alert-dialog-title"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="alert-dialog-title" sx={{ pb: 1 }}>
          {t("user.mfa.removeDevice")}
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          {deviceToDelete && (
            <Box>
              <Box 
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  padding: 2,
                  backgroundColor: 'grey.50',
                  marginBottom: 2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2
                }}
              >
                <PhoneAndroid sx={{ fontSize: 32, color: 'text.secondary' }} />
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {deviceToDelete.label}
                  </Typography>
                  {deviceToDelete.createdDate && (
                    <Typography variant="body2" color="textSecondary">
                      {t("user.mfa.createdOn")}: {formatDate(deviceToDelete.createdDate)}
                    </Typography>
                  )}
                </Box>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 2 }}>
                <Security sx={{ color: 'warning.main', mt: 0.5 }} />
                <Box>
                  <Typography variant="body2" color="textSecondary">
                    {t("user.mfa.removeDeviceWarning")}
                  </Typography>
                </Box>
              </Box>

              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                {t("user.mfa.confirmRemoveAction")}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={closeConfirmDialog} color="secondary" type="button" variant="contained">
            {t("common.back")}
          </Button>
          <Button 
            onClick={() => deviceToDelete && handleRemoveOtp(deviceToDelete.id)} 
            color="error" 
            variant="contained" 
            autoFocus
            startIcon={<Delete />}
          >
            {t("user.mfa.removeDevice")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const mapDispatchToProps = (dispatch: Dispatch<AnyAction>) =>
  bindActionCreators(
    {
      thunkRemoveUserOtpDevice,
    },
    dispatch
  );

const connector = connect(null, mapDispatchToProps);
type ThunkProps = ConnectedProps<typeof connector>;
export default connector(MfaConfiguration);